<template>
  <div>
    <Modal
      :title="newEditTagTitle"
      className="tag-modal small-modal"
      :saving="saving"
      @hide-modal="emit('hide-modal')"
      @submit="handleSave"
    >
      <ErrorDisplay v-if="errorMessage" class="mb-3">
        <span>{{ errorMessage }}</span>
      </ErrorDisplay>
      <TextField
        v-model="formData.label"
        name="Tag Label*"
        placegholder="Enter tag label"
        class="full-width mb-4"
      />
      <TextArea
        v-model="formData.description"
        label="Description"
        placeholder="Enter description..."
        class="full-width mb-4"
        name="Description"
      ></TextArea>
      <span class="label">Color*</span>
      <div class="tag_color mt-1">
        <div v-for="color in availableColors" :key="color" class="color-option">
          <input
            :id="'color-' + color"
            v-model="formData.selectedColor"
            type="radio"
            name="selected_color"
            :value="color"
            class="color-input"
          />
          <label
            :for="'color-' + color"
            class="color-circle"
            :style="{ backgroundColor: color }"
          >
            <i class="fa fa-check"></i>
          </label>
        </div>
      </div>
    </Modal>
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import ErrorDisplay from "@/components/ErrorDisplay.vue";
import Modal from "@/components/Modal.vue";
import TextArea from "@/components/TextArea.vue";
import TextField from "@/components/TextField.vue";
import { Tag } from "@/typings/tag";
import { cloneObj } from "../lib/helpers.js";

const store = useStore();
const props = defineProps({
  tag: {
    type: Tag,
    default: null,
    required: true
  },
  isNew: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(["hide-modal", "save-success", "save-new-success"]);

const formData = ref(cloneObj(props.tag));

const newEditTagTitle = ref("");

const saving = ref(false);

const errorMessage = ref("");

const availableColors = ref("");

const updateAvailableColors = () => {
  const type = props.tag.taggable_type || formData.value.taggable_type;
  availableColors.value = type
    ? window.currentTenant.tags_config[type]?.colors || []
    : [];
};

const loadTagData = async (id: number) => {
  saving.value = true;
  const response = await store.dispatch("fetchTag", id);
  const data = response.data;
  formData.value = cloneObj(data);
  formData.value.selectedColor = data.color;
  if (!props.isNew) formData.value.originalLabel = data.label;
  saving.value = false;
};

const handleSave = async () => {
  saving.value = true;
  try {
    const action = props.isNew ? "createTag" : "updateTag";
    const response = await store.dispatch(action, {
      id: formData.value.id,
      tag: {
        label: formData.value.label.trim(),
        color: formData.value.selectedColor,
        description: formData.value.description,
        taggable_type: formData.value.taggable_type
      }
    });
    const newTag = response.data;
    const emitAction = props.isNew ? "save-new-success" : "save-success";
    emit(emitAction, newTag);
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  formData.value = cloneObj(props.tag);
  updateAvailableColors();
  if (!props.isNew && props.tag.id) {
    loadTagData(props.tag.id);
  }
});

watch(
  [() => props.tag.id, () => props.tag.taggable_type],
  ([id, type]) => {
    if (!props.isNew && id) {
      loadTagData(id);
    } else {
      formData.value = cloneObj(props.tag); // Reset for new tags
    }

    newEditTagTitle.value = `${props.isNew ? "New " : "Edit "} ${
      type ? type.replace(/\b\w/g, (char) => char.toUpperCase()) : ""
    } Tag`;
  },
  { immediate: true }
);
</script>

<style lang="scss">
.tag_color {
  display: flex;
  align-items: center;
  gap: 8px; /* Space between color circles */
}

.color-option {
  position: relative;
}

.color-input {
  display: none !important; /* Hide the radio input */
}

.color-input + .color-circle > i {
  visibility: hidden; /* Hide the checkmark icon */
  color: white;
}

.color-circle {
  width: 25px;
  height: 25px;
  border-radius: 25%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: border-color 0.3s;
  border: 2px solid transparent;
}

.color-input:checked + .color-circle {
  border-color: #007bff; /* Highlight border when selected */
  box-shadow: 0 0 6px rgba(0, 123, 255, 0.5);
}

.color-input:checked + .color-circle > i {
  visibility: visible;
}

.color-circle:hover {
  border-color: rgba(0, 123, 255, 0.5);
}
</style>
